import { useCallback, useState } from 'react';
import {
  getIvtClient,
  RadarDirectionDismissReason,
  RadarDirectionStatus,
  UpdateRadarDirectionStatusCommand,
} from '../../client/IvtHubClient';
import { denialHint } from '../../utils/authorization';

/**
 * Union of the two R19 payload shapes for `UpdateRadarDirectionStatus`
 * (induction §5.2, XIN-ANSWERS §7 2026-10-29):
 *   - `fp`      → `{ status: FALSE_POSITIVE, dismissReason, dismissAssetId? }`
 *                 (`dismissAssetId` is REQUIRED when `dismissReason=COVERED_BY_ASSET`
 *                 and MUST be absent otherwise — the smithy enforces it, so the
 *                 popup also enforces it to avoid a round-trip failure).
 *   - `adopt`  → `{ status: ADOPTED, adoptedAssetId }` — the frontend first
 *                 creates the new asset (NewAssetCreateModal) and then calls
 *                 this hook with the id the create returned (two-step, per
 *                 induction §5.2; the backend does NOT create the asset).
 *
 * Rejected alternatives:
 *   - A single `run(input)` taking the raw smithy shape. Callers would then
 *     have to hand-build the input and pass FALSE_POSITIVE vs ADOPTED fields
 *     without typeguards, so a COVERED_BY_ASSET call missing `dismissAssetId`
 *     would type-check and only fail at the server. Keeping two methods lets
 *     TS enforce the invariant at the call site.
 */
export type RadarFpPayload =
  | { reason: 'NOT_A_DIRECTION' }
  | { reason: 'COVERED_BY_ASSET'; dismissAssetId: string };

export interface RadarDirectionActionState {
  submitting: boolean;
  error: string | null;
  markFalsePositive: (directionId: string, payload: RadarFpPayload) => Promise<boolean>;
  adopt: (directionId: string, adoptedAssetId: string) => Promise<boolean>;
  clearError: () => void;
}

/**
 * UpdateRadarDirectionStatus mutation for the direction detail modal
 * (DAT-9115, R19; induction §5.2).
 *
 * Shape mirrors useLighthouseEdits — a single `submitting` + `error` for the
 * whole modal (one in-flight call at a time: the two buttons are
 * mutually exclusive) and a method per domain action. Returns a bool from each
 * call so the caller can decide what to do next (close the modal, keep the
 * popup open to show the error, etc.) WITHOUT having to peek at the hook's
 * state in the same tick.
 *
 * On SUCCESS the parent reloads the dashboard radar list and closes the
 * modal — the hook does NOT own either, because:
 *   1. the reload target (`useRadarDirections.reload`) is owned by the
 *      parent and is not visible from here; and
 *   2. closing the modal is a parent-state write, not a hook write.
 *
 * Error strategy: a denial maps through `denialHint` to the Chinese hint
 * (same voice as the other dashboard writes). Any other error carries the
 * raw service message with a generic prefix so the popup can render it
 * inline — the FP popup and the adopt flow both display their own error
 * surface rather than letting the error bubble to a global toast.
 */
export function useRadarDirectionAction(): RadarDirectionActionState {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (input: Parameters<typeof mapInput>[0]): Promise<boolean> => {
      setSubmitting(true);
      setError(null);
      try {
        await getIvtClient().send(new UpdateRadarDirectionStatusCommand(mapInput(input)));
        setSubmitting(false);
        return true;
      } catch (e) {
        const denied = denialHint(e);
        setError(denied ?? (e as Error).message ?? '操作失败');
        setSubmitting(false);
        return false;
      }
    },
    [],
  );

  const markFalsePositive = useCallback(
    (directionId: string, payload: RadarFpPayload) =>
      run({ kind: 'fp', directionId, payload }),
    [run],
  );

  const adopt = useCallback(
    (directionId: string, adoptedAssetId: string) =>
      run({ kind: 'adopt', directionId, adoptedAssetId }),
    [run],
  );

  const clearError = useCallback(() => setError(null), []);

  return { submitting, error, markFalsePositive, adopt, clearError };
}

type RunInput =
  | { kind: 'fp'; directionId: string; payload: RadarFpPayload }
  | { kind: 'adopt'; directionId: string; adoptedAssetId: string };

/**
 * Build the smithy-shaped input. Kept as a pure function so the two-shape
 * invariant (dismissAssetId present iff COVERED_BY_ASSET; adoptedAssetId
 * only on ADOPTED) is readable in one place and testable without going
 * through the client.
 */
function mapInput(input: RunInput) {
  if (input.kind === 'fp') {
    const base = {
      directionId: input.directionId,
      status: RadarDirectionStatus.FALSE_POSITIVE,
    };
    if (input.payload.reason === 'NOT_A_DIRECTION') {
      return {
        ...base,
        dismissReason: RadarDirectionDismissReason.NOT_A_DIRECTION,
      };
    }
    return {
      ...base,
      dismissReason: RadarDirectionDismissReason.COVERED_BY_ASSET,
      dismissAssetId: input.payload.dismissAssetId,
    };
  }
  return {
    directionId: input.directionId,
    status: RadarDirectionStatus.ADOPTED,
    adoptedAssetId: input.adoptedAssetId,
  };
}
